import { QueryClient } from '@tanstack/react-query';
import { ContractError } from './errors';
import { isForbidden, isNotFound, isUnauthenticated } from './errors';
/**
 * The one server cache. There is no second transport (§6.5).
 */
export function createQueryClient() {
    return new QueryClient({
        defaultOptions: {
            queries: {
                staleTime: 60_000,
                /*
                 * Never retry a refusal. A 401 is handled by the transport's single
                 * refresh, and a 403 or 404 will refuse identically three more times —
                 * retrying them turns an instant "not found" into three seconds of
                 * spinner before the same answer.
                 */
                retry: (failureCount, error) => {
                    if (isUnauthenticated(error) || isForbidden(error) || isNotFound(error))
                        return false;
                    /*
                     * A contract mismatch is deterministic: the same response fails the
                     * same way three times. Retrying it only delays the error — and
                     * because a retrying query stays `isPending`, it renders as a
                     * skeleton that never resolves rather than as the error it is. That
                     * cost real debugging time on a bad fixture.
                     */
                    if (error instanceof ContractError)
                        return false;
                    return failureCount < 2;
                },
                refetchOnWindowFocus: true,
            },
            mutations: {
                // Writes carry an Idempotency-Key per attempt, so a retry here would
                // reuse it and be deduplicated server-side. We still do not retry
                // automatically: a failed write is something the member should see.
                retry: false,
            },
        },
    });
}
/**
 * Query keys, in one place.
 *
 * A key invented at a call site is a cache nobody can invalidate. Everything
 * that reads goes through here, so an invalidation can name what it means.
 */
export const queryKeys = {
    session: ['session'],
    /*
     * Localised since round 14 — `planName` answers in the caller's language, so
     * the language is part of what was cached.
     *
     * `entitlementAll` is the **prefix**, for invalidation: a plan change has to
     * clear both languages, not only the one the member happens to be reading.
     * Invalidating the exact key would leave the other locale's copy standing
     * for its full `staleTime`.
     */
    entitlement: (locale) => ['entitlement', locale],
    entitlementAll: ['entitlement'],
    /**
     * Content is LOCALE-KEYED, for the same reason `entitlement` is.
     *
     * ── The bug this closes ─────────────────────────────────────────────────
     * Neither key carried the locale, so both languages of an item shared one
     * cache entry. Switching the interface to Français left a list rendering the
     * English titles it had already fetched — the chrome around them translated,
     * the content did not — until something independently refetched. Measured on
     * the case-study list, where the destination pill and the age line came back
     * French beside an English title.
     *
     * `staleTime` decides how long that lasts, which makes it look intermittent:
     * a screen mounted after the switch refetches in the background and corrects
     * itself, and one already mounted does not.
     *
     * `all` stays the bare prefix so an invalidation still clears BOTH languages —
     * the same split as `entitlement` / `entitlementAll`, and for the same
     * reason: a write that changes an item has changed it in every locale.
     */
    content: {
        all: ['content'],
        list: (type, params, locale) => ['content', type, 'list', locale, params],
        one: (type, id, locale) => ['content', type, 'one', locale, id],
    },
    journey: {
        root: ['journey'],
        badges: ['journey', 'badges'],
        challenge: ['journey', 'challenge'],
        leaderboard: ['journey', 'challenge', 'leaderboard'],
        certificates: (locale) => ['journey', 'certificates', locale],
    },
    library: { page: (audience) => ['library', audience ?? 'self'] },
    community: {
        categories: ['community', 'categories'],
        rooms: ['community', 'rooms'],
        room: (id) => ['community', 'rooms', id],
        posts: (roomId, page) => ['community', 'rooms', roomId, 'posts', page],
        /** Under the list's prefix on purpose: one invalidation refreshes both. */
        post: (roomId, postId) => ['community', 'rooms', roomId, 'posts', 'one', postId],
        houseRulesGate: ['community', 'house-rules', 'gate'],
    },
    notifications: { feed: ['notifications'] },
    sos: ['sos'],
    search: (q, types) => ['search', q, types],
    phraseOfDay: ['phrase-rotations', 'day'],
    cohort: { mine: ['cohorts', 'mine'] },
};
//# sourceMappingURL=query-client.js.map